import { useEffect } from 'react';
import { Link, sidebarConfig, useSidebarPinState } from '@backstage/core-components';
import { SearchModal, useSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloudIcon from '@material-ui/icons/Cloud';
import SearchIcon from '@material-ui/icons/Search';
import { LogoFull } from './LogoFull';

export const TOP_BAR_HEIGHT = 56;

const AWS_ACCESS_PORTAL_URL = 'https://plateng.awsapps.com/start/#/?tab=accounts';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: TOP_BAR_HEIGHT,
    zIndex: theme.zIndex.appBar + 1,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr minmax(0, 460px) 1fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 0, 0),
    background: theme.palette.navigation.background,
    '@media print': { display: 'none' },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      gap: theme.spacing(1),
      padding: theme.spacing(0, 1, 0, 0),
    },
  },
  // Width tracks the sidebar's own pinned/closed width (see logoOpen below)
  // so the logo sits in a column exactly as wide as the sidebar beneath it.
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: sidebarConfig.drawerWidthClosed,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shortest,
    }),
    '& svg': { height: '40px !important' },
  },
  logoOpen: {
    width: sidebarConfig.drawerWidthOpen,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    height: 36,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.12)',
    color: '#C9D2F2',
    fontSize: 14,
    cursor: 'pointer',
    textAlign: 'left',
    gridColumn: 3,
    minWidth: 0,
    '&:hover': { background: 'rgba(255,255,255,0.18)' },
    '&:focus-visible': { outline: '2px solid #1DB8F2' },
    [theme.breakpoints.down('xs')]: { gridColumn: 2, justifySelf: 'end' },
  },
  searchLabel: {
    flexGrow: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  kbd: {
    fontSize: 12,
    padding: '1px 6px',
    borderRadius: 4,
    border: '1px solid rgba(255,255,255,0.3)',
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  actions: {
    gridColumn: 4,
    [theme.breakpoints.down('xs')]: { gridColumn: 3 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 8,
    color: '#E4E9FB',
    '&:hover': { background: 'rgba(255,255,255,0.12)' },
    '&:focus-visible': { outline: '2px solid #1DB8F2' },
  },
  create: { border: '1px solid rgba(255,255,255,0.25)' },
  profile: {
    border: '2px solid rgba(255,255,255,0.4)',
    borderRadius: '50%',
    width: 36,
    height: 36,
    '& > *': { margin: 0 },
  },
}));

const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export const TopBar = () => {
  const classes = useStyles();
  const { state, toggleModal } = useSearchModal();
  const { isPinned } = useSidebarPinState();

  // GitLab-style "/" shortcut to focus search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !isTyping(e.target)) {
        e.preventDefault();
        toggleModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleModal]);

  return (
    <>
      <header className={classes.root}>
        <Link
          to="/"
          underline="none"
          aria-label="Home"
          className={`${classes.logo} ${isPinned ? classes.logoOpen : ''}`}
        >
          <LogoFull />
        </Link>
        <button
          type="button"
          className={classes.search}
          onClick={toggleModal}
          aria-label="Search"
        >
          <SearchIcon fontSize="small" />
          <span className={classes.searchLabel}>Search or go to…</span>
          <span className={classes.kbd}>/</span>
        </button>
        <div className={classes.actions}>
          <a
            href={AWS_ACCESS_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AWS access portal (opens in a new tab)"
            title="AWS access portal"
            className={`${classes.action} ${classes.create}`}
          >
            <CloudIcon fontSize="small" />
          </a>
          <Link
            to="/create"
            underline="none"
            aria-label="Create"
            className={`${classes.action} ${classes.create}`}
          >
            <AddIcon fontSize="small" />
          </Link>
          <Link
            to="/settings"
            underline="none"
            aria-label="Profile"
            className={`${classes.action} ${classes.profile}`}
          >
            <UserSettingsSignInAvatar size={32} />
          </Link>
        </div>
      </header>
      <SearchModal {...state} toggleModal={toggleModal} />
    </>
  );
};
